/**
 * Order status (operational) and payment status (financial) badge config.
 * Filled rounded badges, no outline. Order status is more visually prominent than payment.
 */

export type OrderStatusKey = "OPEN" | "READY" | "CLOSED" | "VOIDED";
export type PaymentStatusKey =
  | "PENDING"
  | "AUTHORIZED"
  | "SETTLED"
  | "CAPTURED"
  | "PAID"
  | "FAILED"
  | "VOIDED"
  | "REFUNDED";

export interface BadgeStyle {
  label: string;
  backgroundColor: string;
  color: string;
}

/** Order status: operational workflow. Softer saturation; CLOSED = primary success green. */
export const ORDER_STATUS_BADGES: Record<string, BadgeStyle> = {
  OPEN: {
    label: "Open",
    backgroundColor: "#FFF7ED",
    color: "#C2410C",
  },
  READY: {
    label: "Ready",
    backgroundColor: "#E0F2FE",
    color: "#0369A1",
  },
  CLOSED: {
    label: "Closed",
    backgroundColor: "#E8F5E9",
    color: "#166534",
  },
  VOIDED: {
    label: "Voided",
    backgroundColor: "#FDECEC",
    color: "#B42318",
  },
};

/** Payment status: financial lifecycle. PAID = soft blue to separate from order CLOSED green. */
export const PAYMENT_STATUS_BADGES: Record<string, BadgeStyle> = {
  PENDING: {
    label: "Pending",
    backgroundColor: "#FEF3C7",
    color: "#92400E",
  },
  AUTHORIZED: {
    label: "Authorized",
    backgroundColor: "#EEF2FF",
    color: "#3730A3",
  },
  SETTLED: {
    label: "Settled",
    backgroundColor: "#E0F2FE",
    color: "#0369A1",
  },
  CAPTURED: {
    label: "Captured",
    backgroundColor: "#E6F7EE",
    color: "#166534",
  },
  PAID: {
    label: "Paid",
    backgroundColor: "#E0F2FE",
    color: "#075985",
  },
  FAILED: {
    label: "Failed",
    backgroundColor: "#FDECEC",
    color: "#B42318",
  },
  VOIDED: {
    label: "Voided",
    backgroundColor: "#FDEAEA",
    color: "#991B1B",
  },
  REFUNDED: {
    label: "Refunded",
    backgroundColor: "#F3F4F6",
    color: "#6B7280",
  },
};

const DEFAULT_ORDER_STYLE: BadgeStyle = {
  label: "—",
  backgroundColor: "#F3F4F6",
  color: "#6B7280",
};

const DEFAULT_PAYMENT_STYLE: BadgeStyle = {
  label: "Pending",
  backgroundColor: "#FEF3C7",
  color: "#92400E",
};

/** Muted payment badge when order is VOIDED (no longer financially active). */
const VOIDED_ORDER_PAYMENT_STYLE: BadgeStyle = {
  label: "Pending",
  backgroundColor: "#F3F4F6",
  color: "#6B7280",
};

export function getOrderStatusStyle(status: string | undefined): BadgeStyle {
  if (!status) return DEFAULT_ORDER_STYLE;
  const key = status.toUpperCase();
  return ORDER_STATUS_BADGES[key] ?? { ...DEFAULT_ORDER_STYLE, label: status.replace(/_/g, " ") };
}

export function getPaymentStatusStyle(
  payment: { paymentStatus?: string } | undefined,
  orderStatus?: string
): BadgeStyle {
  if ((orderStatus ?? "").toUpperCase() === "VOIDED") {
    const base = payment?.paymentStatus
      ? PAYMENT_STATUS_BADGES[payment.paymentStatus.toUpperCase()] ?? DEFAULT_PAYMENT_STYLE
      : DEFAULT_PAYMENT_STYLE;
    return { ...VOIDED_ORDER_PAYMENT_STYLE, label: base.label };
  }
  if (!payment?.paymentStatus) return DEFAULT_PAYMENT_STYLE;
  const key = payment.paymentStatus.toUpperCase();
  return PAYMENT_STATUS_BADGES[key] ?? { ...DEFAULT_PAYMENT_STYLE, label: payment.paymentStatus.replace(/_/g, " ") };
}

/** Shared badge layout: inline-flex, 12px, font-weight 500, padding 4px 10px, pill. Order badges use font-semibold for prominence. */
export const BADGE_BASE_CLASS =
  "inline-flex items-center rounded-full text-[12px] font-medium min-w-0";
export const ORDER_BADGE_CLASS = "inline-flex items-center rounded-full text-[12px] font-semibold min-w-0";
export const PAYMENT_BADGE_CLASS = BADGE_BASE_CLASS;

export const BADGE_PADDING_STYLE = { padding: "4px 10px" };
